import React, {useRef, useState} from "react"
import {
	Dialog,
	DialogContent,
	DialogActions,
	Button,
	Typography,
	Box,
	CircularProgress,
	Alert,
	Select,
	MenuItem,
	FormControl,
	InputLabel,
	Divider,
	Chip,
	Radio,
} from "@mui/material"
import {format} from "date-fns"

import {NoteForm, NoteFormHandle} from "../NoteForm"

import {api} from "@indebted/api"
import type {ActiveCase, CaseResolution} from "@indebted/api"
import {useNotification} from "@indebted/components/Notification"
import {useAgent} from "@indebted/hooks/Agent"
import {colors} from "@indebted/theme/colors"

type ResolveCaseModalProps = {
	customerID: string
	activeCase: ActiveCase
	onClose: () => void
	onResolved: () => void
}

function ResolveCaseModal({customerID, activeCase, onClose, onResolved}: ResolveCaseModalProps) {
	const {notification} = useNotification()
	const [agentEmail, agentName] = useAgent()
	const noteFormRef = useRef<NoteFormHandle>(null)

	const [selectedResolution, setSelectedResolution] = useState<CaseResolution | null>(null)
	const reasons = selectedResolution?.Reasons ?? []
	const [selectedReason, setSelectedReason] = useState<string>("")
	const [submitting, setSubmitting] = useState(false)
	const [uploading, setUploading] = useState(false)

	const hasReasons = reasons.length > 0
	const canSubmit = !submitting && !uploading && selectedResolution !== null && (!hasReasons || selectedReason !== "")

	const handleResolutionSelect = (resolution: CaseResolution) => {
		setSelectedResolution(resolution)
		setSelectedReason(resolution.Reasons?.length === 1 ? resolution.Reasons[0].ID : "")
	}

	const noteForms = (selectedResolution?.NoteForms ?? []).map((f) => ({
		Type: f.Type,
		Version: f.Version,
		Fields: f.Fields.map((field) => ({Name: field.Name, Type: field.Type, Required: field.Required})),
	}))

	const handleSubmit = async () => {
		if (!canSubmit || !selectedResolution) return
		setSubmitting(true)

		try {
			let noteID: string | undefined
			if (noteFormRef.current?.hasContent()) {
				noteID = (await noteFormRef.current.createNote()) ?? undefined
			}

			await api.customer.resolveCase(customerID, activeCase.CaseID, {
				Resolution: selectedResolution.ID,
				Reason: selectedReason || undefined,
				NoteID: noteID,
				AgentName: agentName,
				AgentEmail: agentEmail,
			})

			notification.success("Case resolved successfully")
			onResolved()
		} catch (error: any) {
			notification.error(error.Message || "Failed to resolve case")
		} finally {
			setSubmitting(false)
		}
	}

	return (
		<Dialog open onClose={onClose} maxWidth="sm" fullWidth>
			<DialogContent>
				<Box textAlign="center" mb={3} mt={1}>
					<Typography variant="h6" fontWeight={600} color={colors.endlessGalaxy["800"]} mb={0.5}>
						Resolve Case
					</Typography>
					<Chip label={activeCase.CaseType} size="small" variant="outlined" sx={{mt: 1}} />
				</Box>

				<Divider sx={{mb: 2}} />

				<CaseSummaryTable activeCase={activeCase} />

				<Box mt={3}>
					<Typography variant="subtitle1" fontWeight={600}>
						Resolution
					</Typography>
					<Typography variant="body2" color="textSecondary" mb={1.5}>
						Select the outcome for this case.
					</Typography>
					<Box display="flex" flexDirection="column" gap={1.5}>
						{activeCase.Resolutions.map((r) => (
							<Box
								key={r.ID}
								onClick={() => handleResolutionSelect(r)}
								sx={{
									display: "flex",
									alignItems: "center",
									border: `1px solid ${selectedResolution?.ID === r.ID ? colors.warmBlue["400"] : colors.gray["200"]}`,
									borderRadius: 2,
									p: 1.5,
									cursor: "pointer",
									backgroundColor:
										selectedResolution?.ID === r.ID ? colors.warmBlue["50"] : "transparent",
									"&:hover": {
										borderColor: colors.warmBlue["300"],
									},
								}}
							>
								<Radio
									checked={selectedResolution?.ID === r.ID}
									size="small"
									sx={{
										p: 0,
										mr: 1.5,
										color: colors.gray["400"],
										"&.Mui-checked": {color: colors.warmBlue["700"]},
									}}
								/>
								<Box>
									<Typography variant="body2" fontWeight={600}>
										{r.Label}
									</Typography>
									{r.Description && (
										<Typography variant="body2" color="textSecondary">
											{r.Description}
										</Typography>
									)}
								</Box>
							</Box>
						))}
					</Box>
				</Box>

				{hasReasons && (
					<FormControl fullWidth size="small" sx={{mt: 2}}>
						<InputLabel>Reason</InputLabel>
						<Select
							value={selectedReason}
							onChange={(e) => setSelectedReason(e.target.value)}
							label="Reason"
						>
							{reasons.map((r) => (
								<MenuItem key={r.ID} value={r.ID}>
									{r.Label}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				)}

				{selectedResolution && noteForms.length > 0 && (
					<Box mt={3}>
						<Typography variant="subtitle1" fontWeight={600}>
							Closing Notes
						</Typography>
						<Typography variant="body2" color="textSecondary" mb={1}>
							Add any final comments or observations about the case resolution.
						</Typography>
						<NoteForm
							ref={noteFormRef}
							customerID={customerID}
							forms={noteForms}
							attachmentsEnabled
							showSubmitButton={false}
							disabled={submitting}
							onUploadingChange={setUploading}
							pending
						/>
					</Box>
				)}

				{selectedResolution?.ConsequenceDescription && (
					<Alert
						severity={selectedResolution.ConsequenceAlert ? "error" : "warning"}
						sx={{mt: 2, borderRadius: 2}}
						icon={false}
					>
						<Typography
							variant="body2"
							color={selectedResolution.ConsequenceAlert ? colors.error["600"] : colors.warning["700"]}
							textAlign="center"
						>
							{selectedResolution.ConsequenceDescription}
						</Typography>
					</Alert>
				)}
			</DialogContent>
			<DialogActions sx={{px: 3, pb: 3, pt: 2}}>
				<Button
					onClick={onClose}
					variant="outlined"
					disabled={submitting || uploading}
					sx={{flex: 1, borderRadius: 2, color: colors.warmBlue["900"], borderColor: colors.warmBlue["900"]}}
				>
					CANCEL
				</Button>
				<Button
					onClick={handleSubmit}
					variant="contained"
					disabled={!canSubmit}
					sx={{
						flex: 1,
						borderRadius: 2,
						backgroundColor: colors.warmBlue["900"],
						"&:hover": {backgroundColor: colors.warmBlue["800"]},
					}}
				>
					{submitting ? (
						<CircularProgress size={20} color="inherit" />
					) : (
						selectedResolution?.CallToAction || "RESOLVE CASE"
					)}
				</Button>
			</DialogActions>
		</Dialog>
	)
}

function CaseSummaryTable({activeCase}: {activeCase: ActiveCase}) {
	const submittedDate = activeCase.SubmittedAt ? format(new Date(activeCase.SubmittedAt), "MM/dd/yyyy") : ""
	const expiryDate = activeCase.ExpiryDate ? format(new Date(activeCase.ExpiryDate), "MM/dd/yyyy") : ""

	return (
		<Box
			sx={{
				backgroundColor: colors.gray["50"],
				border: `1px solid ${colors.gray["200"]}`,
				borderRadius: 2,
				p: 2.5,
			}}
		>
			<SummaryRow label="Opened By:" value={activeCase.SubmitterName} />
			<SummaryRow label="Opened Date:" value={submittedDate} />
			{expiryDate && <SummaryRow label="Expiration Date:" value={expiryDate} />}
		</Box>
	)
}

function SummaryRow({label, value}: {label: string; value: string}) {
	return (
		<Box display="flex" justifyContent="space-between" py={0.75}>
			<Typography variant="body2" fontWeight={600}>
				{label}
			</Typography>
			<Typography variant="body2">{value}</Typography>
		</Box>
	)
}

export {ResolveCaseModal}
